"use client"

import { useState, useEffect, useRef } from "react"
import dynamic from "next/dynamic"
import { m, AnimatePresence } from "framer-motion"
import { useLocale } from "next-intl"
import { useTranslations } from "@/lib/intl/use-translations"

// Import the constant from the leaflet-free module — importing from
// `@/components/ui/location-map` pulls react-leaflet into the SSR pass and
// trips `ReferenceError: window is not defined` during prerender.
import { CLUSTER_ZOOM_THRESHOLD } from "@/components/ui/location-map-constants"

const LocationMap = dynamic(
  () => import("@/components/ui/location-map").then((m) => m.LocationMap),
  {
    ssr: false,
    // Skeleton color matches the OSM voyager_nolabels base tile tint so
    // that — once the green-tint multiply overlay sits on top — it produces
    // roughly the same visible color as the loaded map. Avoids the white→
    // green pop that the previous bg-neutral-100 caused.
    loading: () => (
      <div
        className="h-full w-full"
        style={{ background: "var(--color-sand-100)" }}
      />
    ),
  }
)
import { Button } from "@/components/ui/button"
import Image from "next/image"
import Link from "next/link"

import { bookingUrlForCountry } from "@/lib/booking"
import { cn } from "@/lib/utils"
import { useLocalePath } from "@/lib/use-localized-navigation"
import { locationsKeys as k } from "@/i18n/locations-keys"
import { publicCountries, getCountryConfigStatic } from "@/i18n/config"
import type { CmsCity, CmsLocation } from "@/lib/cms"
import type { Map as LeafletMap } from "leaflet"
import { JsonLd } from "@/components/ui/json-ld"
import { createMedicalClinicSchema } from "@/lib/schema"
import {
  cityOf,
  isActive,
  resolveCardImage,
  resolveCityDisplayName,
  resolveClinicSlug,
  resolveDisplayName,
  resolveSlug,
  splitComingSoonCities,
} from "@/lib/locations"
import { GERMANY_COORDS, GERMANY_MAJOR_CITIES } from "@/data/germany-border"
import { TabSwitcher } from "@/components/ui/tab-switcher"
import { CountryFlag } from "@/components/ui/navbar/locale-picker"
import { MapPinIcon, ChevronDownIcon } from "@/components/ui/icons"

/** Country flag in a circle — simplified SVG flags */
const FLAG_COLORS: Record<string, { bg: string; stripes: string[] }> = {
  AT: { bg: "#ED2939", stripes: ["#ED2939", "#fff", "#ED2939"] },
  DE: { bg: "#000", stripes: ["#000", "#DD0000", "#FFCE00"] },
  GB: { bg: "#012169", stripes: [] },
  NL: { bg: "#fff", stripes: ["#AE1C28", "#fff", "#21468B"] },
  BE: { bg: "#000", stripes: ["#000", "#FAE042", "#ED2939"] },
  SE: { bg: "#006AA7", stripes: [] },
  FR: { bg: "#fff", stripes: ["#002395", "#fff", "#ED2939"] },
  DK: { bg: "#C8102E", stripes: [] },
}

function CountryFlagCircle({
  code,
  className = "size-5 shrink-0",
}: {
  code: string
  className?: string
}) {
  const flag = FLAG_COLORS[code]
  if (!flag) return null

  // Countries with vertical/horizontal tricolor stripes
  if (flag.stripes.length === 3) {
    const isVertical = code === "FR" || code === "BE"
    return (
      <svg viewBox="0 0 20 20" className={className} aria-hidden="true">
        <defs>
          <clipPath id={`flag-${code}`}>
            <circle cx="10" cy="10" r="10" />
          </clipPath>
        </defs>
        <g clipPath={`url(#flag-${code})`}>
          {isVertical ? (
            <>
              <rect
                x="0"
                y="0"
                width="6.67"
                height="20"
                fill={flag.stripes[0]}
              />
              <rect
                x="6.67"
                y="0"
                width="6.67"
                height="20"
                fill={flag.stripes[1]}
              />
              <rect
                x="13.33"
                y="0"
                width="6.67"
                height="20"
                fill={flag.stripes[2]}
              />
            </>
          ) : (
            <>
              <rect
                x="0"
                y="0"
                width="20"
                height="6.67"
                fill={flag.stripes[0]}
              />
              <rect
                x="0"
                y="6.67"
                width="20"
                height="6.67"
                fill={flag.stripes[1]}
              />
              <rect
                x="0"
                y="13.33"
                width="20"
                height="6.67"
                fill={flag.stripes[2]}
              />
            </>
          )}
        </g>
      </svg>
    )
  }

  // Special flags
  if (code === "GB") {
    return (
      <svg viewBox="0 0 20 20" className={className} aria-hidden="true">
        <defs>
          <clipPath id="flag-GB">
            <circle cx="10" cy="10" r="10" />
          </clipPath>
        </defs>
        <g clipPath="url(#flag-GB)">
          <rect width="20" height="20" fill="#012169" />
          <path d="M0,0 L20,20 M20,0 L0,20" stroke="#fff" strokeWidth="3" />
          <path
            d="M0,0 L20,20 M20,0 L0,20"
            stroke="#C8102E"
            strokeWidth="1.5"
          />
          <path d="M10,0 V20 M0,10 H20" stroke="#fff" strokeWidth="5" />
          <path d="M10,0 V20 M0,10 H20" stroke="#C8102E" strokeWidth="3" />
        </g>
      </svg>
    )
  }

  if (code === "SE") {
    return (
      <svg viewBox="0 0 20 20" className={className} aria-hidden="true">
        <defs>
          <clipPath id="flag-SE">
            <circle cx="10" cy="10" r="10" />
          </clipPath>
        </defs>
        <g clipPath="url(#flag-SE)">
          <rect width="20" height="20" fill="#006AA7" />
          <rect x="6" y="0" width="3" height="20" fill="#FECC00" />
          <rect x="0" y="8" width="20" height="3" fill="#FECC00" />
        </g>
      </svg>
    )
  }

  if (code === "DK") {
    return (
      <svg viewBox="0 0 20 20" className={className} aria-hidden="true">
        <defs>
          <clipPath id="flag-DK">
            <circle cx="10" cy="10" r="10" />
          </clipPath>
        </defs>
        <g clipPath="url(#flag-DK)">
          <rect width="20" height="20" fill="#C8102E" />
          <rect x="6" y="0" width="3" height="20" fill="#fff" />
          <rect x="0" y="8" width="20" height="3" fill="#fff" />
        </g>
      </svg>
    )
  }

  return null
}

/** EU flag in a circle — blue with yellow stars */
function EuFlag() {
  return (
    <svg
      viewBox="0 0 27 27"
      className="size-[27px] shrink-0"
      aria-hidden="true"
    >
      <circle cx="13.5" cy="13.5" r="13.5" fill="#003399" />
      {Array.from({ length: 12 }).map((_, i) => {
        const angle = (i * 30 - 90) * (Math.PI / 180)
        const cx = (13.5 + 9 * Math.cos(angle)).toFixed(3)
        const cy = (13.5 + 9 * Math.sin(angle)).toFixed(3)
        return <circle key={i} cx={cx} cy={cy} r="1.2" fill="#FFCC00" />
      })}
    </svg>
  )
}

interface ComingSoonCityCardProps {
  city: CmsCity
  locale: string
  isExpanded: boolean
  isSubmitted: boolean
  isSubmitting: boolean
  email: string
  error?: string
  onToggle: () => void
  onEmailChange: (value: string) => void
  onSubmit: () => void
}

/**
 * A city we haven't launched yet, with its inline "notify me" waitlist form.
 * Shared by the home-country section (this market's own unlaunched cities) and
 * the rest-of-Europe section (markets we aren't in at all) — the two used to
 * be one hardcoded block, which is how the German cities kept their waitlist on
 * the Swiss site after DE went live (AHD-8830).
 */
function ComingSoonCityCard({
  city,
  locale,
  isExpanded,
  isSubmitted,
  isSubmitting,
  email,
  error,
  onToggle,
  onEmailChange,
  onSubmit,
}: ComingSoonCityCardProps) {
  const t = useTranslations("locations")
  const cityDisplay = resolveCityDisplayName(city, locale)

  return (
    <div className="rounded-[16px] bg-white p-5">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="type-title-lg text-neutral-900">{cityDisplay}</h3>
          <span className="rounded-full bg-neutral-100 px-2.5 py-1 text-xs font-medium text-neutral-500">
            {t(k.comingSoon)}
          </span>
        </div>
        {!isSubmitted ? (
          <button
            onClick={onToggle}
            aria-expanded={isExpanded}
            className="text-aubergine-600 hover:text-aubergine-800 flex cursor-pointer items-center gap-1 text-sm font-medium"
          >
            {t(k.notifyMe)}
            <ChevronDownIcon
              width={14}
              height={14}
              className={cn(
                "transition-transform duration-200",
                isExpanded && "rotate-180"
              )}
            />
          </button>
        ) : (
          <m.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
            className="flex items-center gap-1.5 text-sm font-medium text-green-600"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="8" r="7.5" fill="#7B8C3B" />
              <path
                d="M5 8l2 2 4-4"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {t(k.wellNotifyYou)}
          </m.div>
        )}
      </div>

      {/* safe-animate-presence: conditional-mount — gated by `isExpanded && !isSubmitted` */}
      <AnimatePresence>
        {isExpanded && !isSubmitted && (
          <m.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <form
              className="mt-4 flex flex-col gap-3"
              onSubmit={(e) => {
                e.preventDefault()
                onSubmit()
              }}
            >
              <input
                type="email"
                required
                placeholder={t(k.emailPlaceholder)}
                value={email}
                onChange={(e) => onEmailChange(e.target.value)}
                className="focus-visible:border-aubergine-400 w-full rounded-full border border-neutral-200 bg-white px-4 py-2 text-sm focus-visible:outline-none"
              />
              <Button
                type="submit"
                size="sm"
                disabled={isSubmitting}
                className="w-full"
              >
                {isSubmitting ? (
                  <svg
                    className="animate-spin"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    aria-hidden="true"
                  >
                    <circle
                      cx="8"
                      cy="8"
                      r="6.5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      opacity="0.25"
                    />
                    <path
                      d="M14.5 8a6.5 6.5 0 00-6.5-6.5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                ) : (
                  t(k.joinWaitlistBtn)
                )}
              </Button>
              {error && <p className="px-1 text-xs text-red-600">{error}</p>}
            </form>
          </m.div>
        )}
      </AnimatePresence>
    </div>
  )
}

interface LiveMarketCardProps {
  /** ISO country code of the market, e.g. "DE". */
  code: string
  locale: string
}

/**
 * A market that is already live and public. Replaces what used to be a grid of
 * per-city waitlist cards: the visitor belongs on that market's own site, where
 * its clinics, pricing and booking (or its own waitlist) live.
 */
function LiveMarketCard({ code, locale }: LiveMarketCardProps) {
  const t = useTranslations("locations")
  const lower = code.toLowerCase()
  const config = getCountryConfigStatic(lower)
  if (!config) return null

  return (
    <div className="rounded-[16px] bg-white p-5">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h3 className="type-title-lg flex items-center gap-2 text-neutral-900">
            <CountryFlagCircle code={code} />
            {new Intl.DisplayNames([locale], { type: "region" }).of(code)}
          </h3>
          <span className="bg-deep-green-50 text-deep-green-700 rounded-full px-2.5 py-1 text-xs font-medium">
            {t(k.liveMarketPill)}
          </span>
        </div>
        <Link
          href={`/${lower}/${config.defaultLanguage}/locations`}
          className="text-aubergine-600 hover:text-aubergine-800 flex cursor-pointer items-center gap-1 text-sm font-medium whitespace-nowrap"
        >
          {t(k.liveMarketCta)}
        </Link>
      </div>
    </div>
  )
}

interface LocationsClientProps {
  initialCmsLocations: CmsLocation[]
  cmsCities: CmsCity[]
  country: string
}

export default function LocationsClient({
  initialCmsLocations,
  cmsCities,
  country,
}: LocationsClientProps) {
  const t = useTranslations("locations")
  const locale = useLocale()
  const lp = useLocalePath()
  const [activeTab, setActiveTab] = useState("switzerland")
  const [expandedCity, setExpandedCity] = useState<string | null>(null)
  const [submittedCities, setSubmittedCities] = useState<Set<string>>(new Set())
  const [submittingCities, setSubmittingCities] = useState<Set<string>>(
    new Set()
  )
  const [emailInputs, setEmailInputs] = useState<Record<string, string>>({})
  const swissRef = useRef<HTMLElement>(null)
  const europeRef = useRef<HTMLElement>(null)
  const mapInstanceRef = useRef<LeafletMap | null>(null)
  const [isZoomedIn, setIsZoomedIn] = useState(false)
  // Reserve enough vertical space at the top of the map for navbar +
  // heroLabel + h1 + subtitle + tab switcher, so the FitBounds call places
  // Switzerland strictly below them. The actual height varies by viewport
  // width (h1/subtitle wrap) and locale (DE/FR strings are longer), so we
  // measure the rendered hero text container instead of hardcoding a value
  // — which is what hid Schaffhausen behind the tab switcher at narrow
  // viewports even after the devZoom fix.
  const heroContentRef = useRef<HTMLDivElement>(null)
  const [mapPadTop, setMapPadTop] = useState(360)

  const clinics = initialCmsLocations
    .filter(isActive)
    .filter(
      (l) =>
        l.clinic &&
        l.address &&
        // `!= null` would let a present-but-non-finite coord through to
        // Leaflet as `(NaN, NaN)` and crash the map — require a real number.
        Number.isFinite(l.lat) &&
        Number.isFinite(l.lng) &&
        l.type
    )
    .map((l) => {
      const cs = resolveClinicSlug(l) ?? ""
      const slug = resolveSlug(l)
      const city = cityOf(l)
      return {
        id: l.id,
        city: city.city,
        country: city.country,
        cityDisplay: resolveDisplayName(l, locale),
        clinic: l.clinic as string,
        address: l.address as string,
        type: l.type as "mri" | "blood",
        lat: l.lat as number,
        lng: l.lng as number,
        href: lp(`/locations/${slug}/${cs}`),
        cardImage: resolveCardImage(l),
      }
    })

  // Split by product so a market that only has one of them (DE books blood,
  // no MRI clinic yet) doesn't render an empty section under a heading.
  const mriClinics = clinics.filter((c) => c.type === "mri")
  const bloodClinics = clinics.filter((c) => c.type === "blood")

  // Coming-soon cities, split three ways (AHD-8830):
  //  - `homeComingSoon` — this market's own cities. A market can be live in one
  //    city and still coming soon elsewhere (DE books blood in Munich while
  //    Berlin/Hamburg wait), so these keep their per-city waitlist and render
  //    with the home-country clinics rather than under "rest of Europe".
  //  - `liveMarketGroups` — another country that is itself a live public
  //    market. Offering a waitlist for a market that already sells is the bug:
  //    send the visitor to that market's own site instead. Keys off
  //    `publicCountries` so a market converts the day it goes public — no
  //    per-country branch to remember (this replaced hardcoded `country ===
  //    "de"` / `!== "NL"` special cases, and closes AHD-6855 for NL).
  //  - `waitlistGroups` — everywhere we aren't live yet: unchanged waitlist.
  const {
    home: homeComingSoon,
    liveMarkets: liveMarketGroups,
    waitlist: waitlistGroups,
  } = splitComingSoonCities(cmsCities, country, (code) =>
    publicCountries.some((c) => c.code === code.toLowerCase())
  )

  // Coming-soon pins on the /de/de map: match CMS DE coming-soon cities
  // against GERMANY_MAJOR_CITIES for coords. Click scrolls to the
  // "Restliches Europa" cards so the visitor can join a per-city waitlist.
  const deComingSoonPins =
    country === "de"
      ? cmsCities
          .filter((c) => c.country === "DE" && c.status === "coming-soon")
          .map((c) => {
            const coords = GERMANY_MAJOR_CITIES.find((m) => m.name === c.city)
            return coords
              ? {
                  id: c.slug,
                  label: c.displayName ?? c.city,
                  lat: coords.lat,
                  lng: coords.lng,
                }
              : null
          })
          .filter((x): x is NonNullable<typeof x> => x !== null)
      : []

  useEffect(() => {
    const el = heroContentRef.current
    if (!el) return
    const update = () => {
      const section = el.parentElement
      if (!section) return
      const bottomFromSectionTop =
        el.getBoundingClientRect().bottom - section.getBoundingClientRect().top
      setMapPadTop(Math.ceil(bottomFromSectionTop + 24))
    }
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    window.addEventListener("resize", update)
    return () => {
      ro.disconnect()
      window.removeEventListener("resize", update)
    }
  }, [])

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveTab(
              entry.target.id === "switzerland-section"
                ? "switzerland"
                : "europe"
            )
          }
        })
      },
      { rootMargin: "-200px 0px -50% 0px", threshold: 0 }
    )
    if (swissRef.current) observer.observe(swissRef.current)
    if (europeRef.current) observer.observe(europeRef.current)
    return () => observer.disconnect()
  }, [])

  function handleTabChange(value: string) {
    setActiveTab(value)
    const id =
      value === "switzerland" ? "switzerland-section" : "europe-section"
    const el = document.getElementById(id)
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 140
      window.scrollTo({ top: y, behavior: "smooth" })
    }
  }

  const [cityErrors, setCityErrors] = useState<Record<string, string>>({})

  async function handleWaitlistSubmit(cityLoc: CmsCity) {
    const city = cityLoc.city
    const email = emailInputs[city]?.trim()
    if (!email) return
    setCityErrors((prev) => ({ ...prev, [city]: "" }))
    setSubmittingCities((prev) => new Set(prev).add(city))

    try {
      const res = await fetch("/api/waitlist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          city,
          locale,
          // The city's own country, not the page's. Someone asking to be
          // notified about Vienna wants Austria, whichever site they happened
          // to be reading — this used to stamp the page country, so every
          // foreign-city signup from /ch/* landed in the CRM labelled CH.
          country: cityLoc.country.toLowerCase(),
          source: "locations-page",
        }),
      })
      if (!res.ok) {
        const msg =
          res.status === 429 ? t("waitlistRateLimit") : t("waitlistError")
        setCityErrors((prev) => ({ ...prev, [city]: msg }))
        return
      }
    } catch {
      setCityErrors((prev) => ({ ...prev, [city]: t("waitlistError") }))
      return
    } finally {
      setSubmittingCities((prev) => {
        const next = new Set(prev)
        next.delete(city)
        return next
      })
    }

    setSubmittedCities((prev) => new Set(prev).add(city))
    setExpandedCity(null)
  }

  return (
    <>
      {clinics
        .filter((c) => c.type === "mri")
        .map((clinic) => (
          <JsonLd
            key={clinic.id}
            data={createMedicalClinicSchema({
              name: clinic.clinic,
              address: clinic.address,
              city: clinic.city,
              country: clinic.country,
              type: clinic.type,
              lat: clinic.lat,
              lng: clinic.lng,
            })}
          />
        ))}

      {/* Map tile color adjustments — scoped to locations hero */}
      <style>{`.locations-hero-map .leaflet-tile-pane img { filter: saturate(0.48) !important; }`}</style>

      {/* Hero — map fills the entire section behind text */}
      <section className="relative h-[calc(100dvh-108px)] overflow-hidden">
        {/* Layer 0: Full-bleed map. No opacity fade-in — the dynamic-import
            `loading` skeleton already paints a tinted div that matches the
            loaded look, so when Leaflet's first tiles render we want them
            to appear immediately rather than tween-fade on top. */}
        <div className="locations-hero-map absolute inset-0 z-0">
          <LocationMap
            clinics={clinics}
            fullBleed
            mapPadTop={mapPadTop}
            mapPad={0.08}
            tileTint={{ color: "#4fa176", opacity: 0.3 }} // ds-color-ignore: translucent overlay
            {...(country === "de"
              ? {
                  borderData: GERMANY_COORDS,
                  centerCoords: [51.2, 10.5] as [number, number],
                  defaultZoom: 5,
                  // German context labels instead of the default Swiss
                  // MAJOR_CITIES, so Basel/Schaffhausen/Winterthur don't
                  // overlap the bottom legend on the /de/de map.
                  // Only label cities where we have a clinic or a coming-soon
                  // site — a bare "Köln" on the map reads as a location we
                  // don't have.
                  majorCities: GERMANY_MAJOR_CITIES.filter((m) =>
                    [
                      ...clinics.flatMap((c) => [c.city, c.cityDisplay]),
                      ...deComingSoonPins.map((p) => p.label),
                    ].includes(m.name)
                  ),
                  comingSoonCities: deComingSoonPins,
                  cityMarkerType: "de-coming-soon" as const,
                  // DE has one active clinic (Munich) alongside coming-soon
                  // cities — opt into rendering both layers instead of the
                  // legacy NL/AT behavior where coming-soon replaces clinics.
                  overlayComingSoonOnClinics: true,
                  onCityClick: () => {
                    const el = document.getElementById("europe-section")
                    if (!el) return
                    const y =
                      el.getBoundingClientRect().top + window.scrollY - 100
                    window.scrollTo({ top: y, behavior: "smooth" })
                  },
                }
              : {})}
            mapRefCallback={(map) => {
              mapInstanceRef.current = map
              if (map) {
                map.on("zoomend", () =>
                  setIsZoomedIn(map.getZoom() >= CLUSTER_ZOOM_THRESHOLD)
                )
              }
            }}
            onClinicClick={(id) => {
              const el = document.getElementById(`clinic-${id}`)
              if (el) {
                const y = el.getBoundingClientRect().top + window.scrollY - 140
                window.scrollTo({ top: y, behavior: "smooth" })
              }
            }}
          />
        </div>

        {/* Layer 1: Top gradient for text readability */}
        <div
          className="pointer-events-none absolute inset-x-0 top-0 z-[1] h-[260px]"
          style={{
            background:
              "linear-gradient(to bottom, #f4f4f4 20%, transparent 100%)",
          }}
        />

        {/* Layer 2: Text content */}
        <div
          ref={heroContentRef}
          className="pointer-events-none relative z-[2] pt-6 text-center md:pt-8"
        >
          <div className="mx-auto max-w-[1440px] px-4 md:px-10">
            {/* Hero is paint-critical for LCP — keep visible from the
                first frame. Translate-only animation so Lighthouse / CrUX
                count the H1 as the LCP element. */}
            <p className="type-label-lg pointer-events-auto text-neutral-400">
              {t(k.heroLabel)}
            </p>
            <h1 className="type-display-lg pointer-events-auto mx-auto mt-4 max-w-3xl text-center text-balance text-neutral-900">
              {t(k.heroHeading)}
            </h1>
            <p className="type-body-lg pointer-events-auto mx-auto mt-4 max-w-2xl text-neutral-600">
              {t(k.heroSubtitle)}
            </p>
          </div>

          {/* Tab switcher — labels + flag are country-aware so that the
              Germany locations page reads "Deutschland / Übrige EU" with
              a German-flag chip, while Switzerland keeps its "Schweiz /
              Übriges Europa" pairing. The `value` strings stay stable
              ("switzerland" / "europe") so existing state + analytics
              don't churn. */}
          <div className="pointer-events-auto mt-8 flex justify-center">
            <TabSwitcher
              tabs={[
                {
                  value: "switzerland",
                  label:
                    country === "de" ? t(k.tabGermany) : t(k.tabSwitzerland),
                  icon:
                    country === "de" ? (
                      <CountryFlagCircle
                        code="DE"
                        className="size-[27px] shrink-0"
                      />
                    ) : (
                      <span className="flex size-[27px] items-center justify-center overflow-hidden rounded-full bg-[#D52B1E]">
                        {" "}
                        {/* ds-color-ignore: third-party / brand logo colour */}
                        <CountryFlag flag="ch" className="size-4" />
                      </span>
                    ),
                },
                {
                  value: "europe",
                  label:
                    country === "de" ? t(k.tabRestOfEu) : t(k.tabRestOfEurope),
                  icon: <EuFlag />,
                },
              ]}
              activeTab={activeTab}
              onChange={handleTabChange}
            />
          </div>
        </div>

        {/* Zoom controls — below tab switcher, right side */}
        <div
          className="absolute top-[55%] right-4 z-[2] flex flex-col overflow-hidden rounded-full md:top-[250px] md:right-6"
          style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.12)" }}
        >
          <button
            type="button"
            onClick={() => mapInstanceRef.current?.zoomIn()}
            aria-label="Zoom in on map"
            className="flex h-[52px] w-[52px] cursor-pointer items-center justify-center border-b border-neutral-200/80 bg-white text-neutral-800 hover:bg-neutral-50"
          >
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
              <path
                d="M11 4v14M4 11h14"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
              />
            </svg>
          </button>
          <button
            type="button"
            onClick={() => {
              const map = mapInstanceRef.current
              if (!map) return
              if (map.getZoom() >= CLUSTER_ZOOM_THRESHOLD) {
                map.fire("returnToCountry")
              } else {
                map.zoomOut()
              }
            }}
            aria-label="Zoom out on map"
            className="flex h-[52px] w-[52px] cursor-pointer items-center justify-center bg-white text-neutral-800 hover:bg-neutral-50"
          >
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
              <path
                d="M4 11h14"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* Back to country button — visible when zoomed in */}
        {isZoomedIn && (
          <m.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            type="button"
            onClick={() => mapInstanceRef.current?.fire("returnToCountry")}
            aria-label="Return map to country view"
            className="absolute top-[calc(55%+120px)] right-4 z-[2] flex h-[52px] w-[52px] cursor-pointer items-center justify-center rounded-full bg-white text-neutral-800 hover:bg-neutral-50 md:top-[370px] md:right-6"
            style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.12)" }}
            title="Back to country view"
          >
            <svg
              width="22"
              height="22"
              viewBox="0 -960 960 960"
              fill="currentColor"
            >
              <path d="M280-200v-80h284q63 0 109.5-40T720-420q0-60-46.5-100T564-560H312l104 104-56 56-200-200 200-200 56 56-104 104h252q97 0 166.5 63T800-420q0 94-69.5 157T564-200H280Z" />
            </svg>
          </m.button>
        )}

        {/* Legend — bottom of hero */}
        <m.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="pointer-events-none absolute inset-x-0 bottom-3 z-[2] flex items-center justify-center gap-6"
        >
          {/* Legend only lists products this market has a pin for — DE has
              no MRI site, so "Ganzkörper-Check-up" would filter to nothing. */}
          {mriClinics.length > 0 && (
            <div className="flex items-center gap-1.5">
              <span
                className="inline-block h-2.5 w-2.5 rounded-full"
                style={{
                  backgroundColor: "#182D56",
                  boxShadow: "0 0 0 2px rgba(255,255,255,0.5)",
                }}
              />
              <span
                className="type-body-sm font-medium text-neutral-700"
                style={{
                  textShadow:
                    "0 0 4px rgba(255,255,255,0.8), 0 0 8px rgba(255,255,255,0.5)",
                }}
              >
                {t(k.fullBodyCheckupBadge)}
              </span>
            </div>
          )}
          <div className="flex items-center gap-1.5">
            <span
              className="inline-block h-2.5 w-2.5 rounded-full"
              style={{
                backgroundColor: "#E96F3F",
                boxShadow: "0 0 0 2px rgba(255,255,255,0.5)",
              }}
            />
            <span
              className="type-body-sm font-medium text-neutral-700"
              style={{
                textShadow:
                  "0 0 4px rgba(255,255,255,0.8), 0 0 8px rgba(255,255,255,0.5)",
              }}
            >
              {t(k.legendBlood)}
            </span>
          </div>
          {country === "de" && (
            <div className="flex items-center gap-1.5">
              <span
                className="inline-block h-2.5 w-2.5 rounded-full"
                style={{
                  // sand-400 — matches pin-de-coming-soon.svg inner circle
                  backgroundColor: "#c8b59d", // ds-color-ignore: matches static SVG asset color
                  boxShadow: "0 0 0 2px rgba(255,255,255,0.5)",
                }}
              />
              <span
                className="type-body-sm font-medium text-neutral-700"
                style={{
                  textShadow:
                    "0 0 4px rgba(255,255,255,0.8), 0 0 8px rgba(255,255,255,0.5)",
                }}
              >
                {t(k.legendComingSoon)}
              </span>
            </div>
          )}
        </m.div>
      </section>

      {/* Home-country section: this market's live clinics, then its own cities
          we haven't opened yet. A market can be part-launched (DE books blood in
          Munich while Berlin/Hamburg are still waitlist), so the two belong
          together under the country heading — a home city listed under "rest of
          Europe" reads as though we don't consider it part of the market.
          Still hidden entirely when the market has no clinics: a bare heading
          over empty grids reads as broken UX. */}
      {clinics.length > 0 && (
        <section
          id="switzerland-section"
          ref={swissRef}
          className="pt-12 pb-16"
        >
          <div className="mx-auto max-w-[1440px] px-4 md:px-10">
            <div>
              <div className="mb-8 flex items-center gap-3">
                <h2 className="type-display-sm leading-none text-neutral-900">
                  {t(k.switzerlandHeading)}
                </h2>
                <span className="rounded-full bg-neutral-100 px-3 py-1 text-sm font-medium text-neutral-600">
                  {t(k.locationCount, { count: clinics.length })}
                </span>
              </div>
            </div>

            {/* MRI clinics */}
            {mriClinics.length > 0 && (
              <>
                <p className="type-label-md text-neutral-400">
                  {t(k.mriSectionLabel)}
                </p>
                <p className="type-body-md mt-2 mb-4 text-neutral-500">
                  {t(k.fullBodyCheckupNote)}
                </p>
              </>
            )}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {mriClinics.map((clinic) => {
                const clinicHref = clinic.href
                const image = clinic.cardImage
                return (
                  <div key={clinic.id}>
                    <div
                      id={`clinic-${clinic.id}`}
                      className="rounded-[16px] bg-white p-6"
                    >
                      <div className="flex items-start gap-4">
                        {image && (
                          <Link href={clinicHref} className="flex-shrink-0">
                            <div className="relative h-16 w-16 overflow-hidden rounded-[10px] bg-neutral-100">
                              <Image
                                src={image}
                                alt={clinic.city}
                                fill
                                sizes="64px"
                                className="object-cover"
                              />
                            </div>
                          </Link>
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-col gap-4">
                            <div>
                              <Link
                                href={clinicHref}
                                className="hover:underline"
                              >
                                <h3 className="type-title-lg text-neutral-900">
                                  {clinic.cityDisplay}
                                </h3>
                              </Link>
                              <p className="type-body-lg text-neutral-600">
                                {clinic.clinic}
                              </p>
                              <div className="mt-2 flex flex-wrap gap-1.5">
                                <span
                                  className="inline-block rounded-full px-2 py-0.5 text-xs font-medium"
                                  style={{
                                    backgroundColor:
                                      "var(--color-light-blue-50)",
                                    color: "var(--color-bright-blue-700)",
                                  }}
                                >
                                  {t(k.fullBodyCheckupBadge)}
                                </span>
                              </div>
                              <p className="type-body-md mt-2 flex items-center gap-1.5 text-neutral-500">
                                <MapPinIcon className="flex-shrink-0" />
                                {clinic.address}
                              </p>
                            </div>
                            <a href={bookingUrlForCountry(country, locale)}>
                              <Button
                                variant="outline"
                                className="mt-1 flex-shrink-0"
                              >
                                {t(k.bookCheckup)}
                              </Button>
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Blood test locations */}
            {bloodClinics.length > 0 && (
              <p className="type-label-md mt-10 mb-4 text-neutral-400">
                {t(k.bloodSectionLabel)}
              </p>
            )}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {bloodClinics.map((clinic) => {
                const clinicHref = clinic.href
                const image = clinic.cardImage
                return (
                  <div key={clinic.id}>
                    <div
                      id={`clinic-${clinic.id}`}
                      className="rounded-[16px] bg-white p-6"
                    >
                      <div className="flex items-start gap-4">
                        {image && (
                          <Link href={clinicHref} className="flex-shrink-0">
                            <div className="relative h-16 w-16 overflow-hidden rounded-[10px] bg-neutral-100">
                              <Image
                                src={image}
                                alt={clinic.city}
                                fill
                                sizes="64px"
                                className="object-cover"
                              />
                            </div>
                          </Link>
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-col gap-4">
                            <div>
                              <Link
                                href={clinicHref}
                                className="hover:underline"
                              >
                                <h3 className="type-title-lg text-neutral-900">
                                  {clinic.cityDisplay}
                                </h3>
                              </Link>
                              <p className="type-body-lg text-neutral-600">
                                {clinic.clinic}
                              </p>
                              <span
                                className="mt-2 inline-block rounded-full px-2 py-0.5 text-xs font-medium"
                                style={{
                                  backgroundColor: "var(--color-error-100)",
                                  color: "#E96F3F",
                                }}
                              >
                                {t(k.bloodTestBadge)}
                              </span>
                              <p className="type-body-md mt-2 flex items-center gap-1.5 text-neutral-500">
                                <MapPinIcon className="flex-shrink-0" />
                                {clinic.address}
                              </p>
                            </div>
                            <a href={bookingUrlForCountry(country, locale)}>
                              <Button
                                variant="outline"
                                className="mt-1 flex-shrink-0"
                              >
                                {t(k.bookCheckup)}
                              </Button>
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>

            {/* This market's own cities that haven't opened yet — same
                per-city waitlist, kept under the country heading rather than
                exiled to "rest of Europe". */}
            {homeComingSoon.length > 0 && (
              <>
                <p className="type-label-md mt-10 text-neutral-400">
                  {t(k.comingSoon)}
                </p>
                <p className="type-body-md mt-2 mb-4 text-neutral-500">
                  {t(k.europeSubtitle)}
                </p>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {homeComingSoon.map((cityLoc) => (
                    <ComingSoonCityCard
                      key={cityLoc.id}
                      city={cityLoc}
                      locale={locale}
                      isExpanded={expandedCity === cityLoc.city}
                      isSubmitted={submittedCities.has(cityLoc.city)}
                      isSubmitting={submittingCities.has(cityLoc.city)}
                      email={emailInputs[cityLoc.city] || ""}
                      error={cityErrors[cityLoc.city]}
                      onToggle={() =>
                        setExpandedCity(
                          expandedCity === cityLoc.city ? null : cityLoc.city
                        )
                      }
                      onEmailChange={(value) =>
                        setEmailInputs((prev) => ({
                          ...prev,
                          [cityLoc.city]: value,
                        }))
                      }
                      onSubmit={() => handleWaitlistSubmit(cityLoc)}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </section>
      )}

      {/* Rest of Europe: markets that are already live get a link to their own
          site; everywhere else keeps the per-city waitlist. */}
      <section id="europe-section" ref={europeRef} className="pt-12 pb-16">
        <div className="mx-auto max-w-[1440px] px-4 md:px-10">
          <div>
            <h2 className="type-display-sm text-neutral-900">
              {t(k.europeHeading)}
            </h2>
          </div>

          {liveMarketGroups.length > 0 && (
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {liveMarketGroups.map((group) => (
                <LiveMarketCard
                  key={group.country}
                  code={group.country}
                  locale={locale}
                />
              ))}
            </div>
          )}

          {waitlistGroups.length > 0 && (
            <p className="type-body-lg mt-8 text-neutral-600">
              {t(k.europeSubtitle)}
            </p>
          )}

          {waitlistGroups.map((group) => (
            <div key={group.country} className="mt-10">
              <p className="mb-3 flex items-center gap-2 text-sm font-medium text-neutral-400">
                <CountryFlagCircle code={group.country} />{" "}
                {new Intl.DisplayNames([locale], { type: "region" }).of(
                  group.country
                )}
              </p>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {group.cities.map((cityLoc) => (
                  <ComingSoonCityCard
                    key={cityLoc.id}
                    city={cityLoc}
                    locale={locale}
                    isExpanded={expandedCity === cityLoc.city}
                    isSubmitted={submittedCities.has(cityLoc.city)}
                    isSubmitting={submittingCities.has(cityLoc.city)}
                    email={emailInputs[cityLoc.city] || ""}
                    error={cityErrors[cityLoc.city]}
                    onToggle={() =>
                      setExpandedCity(
                        expandedCity === cityLoc.city ? null : cityLoc.city
                      )
                    }
                    onEmailChange={(value) =>
                      setEmailInputs((prev) => ({
                        ...prev,
                        [cityLoc.city]: value,
                      }))
                    }
                    onSubmit={() => handleWaitlistSubmit(cityLoc)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20 text-center">
        <div className="mx-auto max-w-[1440px] px-4 md:px-10">
          <div>
            <h2 className="type-display-md text-neutral-900">
              {t(k.ctaHeading)}
            </h2>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <a href={bookingUrlForCountry(country, locale)}>
                <Button>{t(k.ctaBookCheckup)}</Button>
              </a>
              <Link href={lp("/contact")}>
                <Button variant="outline">{t(k.ctaTalkToTeam)}</Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
